import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DialogConfirmCancel from '../../../../dialog/DialogConfirm';
import useDevelopmentalRubrics from '../../../../../hooks/developmental-rubrics/useDevelopmentalRubrics';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updateDevelopmentalRubricDialog } from '../../../../../store/actions/developmental-rubrics.actions';
import InputField from '../../../../FormControl/input/InputField';
import CardKeyValue from '../../../../card/cardKeyValue';
import useGranularSteps from '../../../../../hooks/developmental-rubrics/useGranularStep';

const DialogAction = ({ show, onClose, type, formData = {}, curriculumId }) => {
  const types = [
    'archive',
    'delete',
    'clone',
    'unlink',
    'unlink-class',
    'link',
    'edit notes',
    'created-granular-step',
    'updated-granular-step',
    'deleted-granular-step'
  ];
  const { studentId } = useParams();
  const redirectStudentId = studentId ? `${studentId}/` : '';
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, toggleLoading] = useState(false);
  const [errors, setInputErrors] = useState({});
  const [form, setInputForm] = useState(formData);
  const {
    deleteDevelopmentalRubric,
    archiveDevelopmentalRubric,
    linkDevelopmentalRubric,
    unlinkDevelopmentalRubric,
    unlinkDevelopmentalRubricFromClass,
    cloneDevelopmentalRubric,
    updateDevelopmentalRubric
  } = useDevelopmentalRubrics();

  const { createGranularStep, updateGranularStep, deleteGranularStep } = useGranularSteps();

  const onChange = (name, value) => {
    setInputForm({ ...form, [name]: value });
    setInputErrors({ ...errors, [name]: null });
  };
  const onConfirm = async (e) => {
    if (!types.includes(type)) {
      setInputErrors({ message: 'Invalid type' });
      return;
    }
    if (form._id === undefined && type !== 'unlink-class') {
      setInputErrors({ message: 'Invalid id' });
      return;
    }
    e.preventDefault();
    toggleLoading(true);
    try {
      let result = { error: false };
      if (type === 'delete') {
        result = await deleteDevelopmentalRubric(curriculumId, form._id);
      }
      if (type === 'archive') {
        result = await archiveDevelopmentalRubric(curriculumId, form._id);
      }
      if (type === 'clone') {
        result = await cloneDevelopmentalRubric(curriculumId, form._id, form.email);
      }
      if (type === 'edit notes') {
        result = await updateDevelopmentalRubric(curriculumId, form._id, 'notes', form);
      }

      if (type === 'link') {
        result = await linkDevelopmentalRubric(curriculumId, form._id);
      }

      if (type === 'unlink') {
        result = await unlinkDevelopmentalRubric(curriculumId, form._id);
      }

      if (type === 'unlink-class') {
        result = await unlinkDevelopmentalRubricFromClass(curriculumId, form.drId, form.classId);
      }

      if (type === 'created-granular-step') {
        result = await createGranularStep(curriculumId, form.drId, form.lcId, form);
      }
      if (type === 'updated-granular-step') {
        result = await updateGranularStep(curriculumId, form.drId, form.lcId, form._id, form);
      }
      if (type === 'deleted-granular-step') {
        result = await deleteGranularStep(curriculumId, form.drId, form.lcId, form._id);
      }

      if (result.error) {
        setInputErrors(result);
      } else {
        if (type === 'delete' || type === 'archive') {
          navigate(`/dashboard/${redirectStudentId}developmental-rubrics/${curriculumId}/list`);
        }
        onClose();
      }
    } finally {
      toggleLoading(false);
    }
  };

  useEffect(() => {
    setInputForm(formData);
    if (!formData._id && type !== 'created-granular-step' && type !== 'unlink-class') {
      setInputErrors({ message: 'Invalid id' });
    }
  }, [formData]);

  const checkConfirm = () => {
    if (types.includes(type) && form._id) {
      return true;
    }

    if (type === 'created-granular-step') {
      return true;
    }

    if (type === 'unlink-class' && form.drId && form.classId) {
      return true;
    }

    return false;
  };

  return (
    <DialogConfirmCancel
      id={`developmental-rubrics-${type}-${form?._id}`}
      open={show}
      onCancel={onClose}
      onConfirm={checkConfirm() ? onConfirm : undefined}
      loading={loading}
      title={type.replace(/-/g, ' ')}
      onCloseHeader={true}
      maxWidth='xs'
    >
      {['archive', 'delete', 'clone', 'unlink', 'link'].includes(type) && (
        <div>
          {type === 'unlink' && (
            <div className='flex flex-wrap gap-1 pb-4 items-center justify-center'>
              {form?.linkedClasses?.map((item, index) => (
                <div key={index} className='relative'>
                  <CardKeyValue
                    title='Class'
                    className={`min-w-100px max-w-150px`}
                    value={
                      <div className='flex items-center gap-1'>
                        {item.code}
                        <Tooltip title={`Unlink ${item.code}`}>
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() =>
                              dispatch(
                                updateDevelopmentalRubricDialog(true, 'unlink-class', {
                                  drId: form._id,
                                  classId: item._id,
                                  className: item.code,
                                  drName: form.name
                                })
                              )
                            }
                          >
                            <LinkOffIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </div>
                    }
                  />
                </div>
              ))}
            </div>
          )}
          <Typography>
            Are you sure you want to {type} the developmental rubric <b>{form.name}</b>?
          </Typography>
        </div>
      )}
      {type === 'unlink-class' && (
        <div>
          <Typography>
            Are you sure you want to unlink class <b>{form.className}</b> from developmental rubric{' '}
            <b>{form.drName}</b>?
          </Typography>
          <Alert severity='warning' className='mt-2'>
            All student marking data for this class in this DR will be removed.
          </Alert>
        </div>
      )}
      {['edit notes'].includes(type) && (
        <div className='grid grid-cols-4 items-start gap-4'>
          <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
            Notes
          </Typography>
          <div className='col-span-3'>
            <InputField
              id='notes'
              name='notes'
              value={form.notes}
              error={errors?.notes}
              helperText={errors?.notes}
              onChange={(e) => onChange('notes', e.target.value)}
              fullWidth
              multiline={true}
            />
          </div>
        </div>
      )}
      {['created-granular-step', 'updated-granular-step'].includes(type) && (
        <div className='grid grid-cols-4 items-start gap-4'>
          <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
            Definition
          </Typography>
          <div className='col-span-3'>
            <InputField
              id='definition'
              name='definition'
              value={form.definition}
              error={errors?.definition}
              helperText={errors?.definition}
              onChange={(e) => onChange('definition', e.target.value)}
              fullWidth
              multiline={true}
            />
          </div>
        </div>
      )}
      {['deleted-granular-step'].includes(type) && (
        <Typography>
          Are you sure you want to delete the granular step{' '}
          <b>
            {'"'}
            {form.definition}
            {'"'}
          </b>
          ?
        </Typography>
      )}
      {errors?.message && (
        <Alert severity='error' className='mt-2'>
          {errors?.message}
        </Alert>
      )}
    </DialogConfirmCancel>
  );
};

export default DialogAction;
